import React, { useEffect, useRef, useState, useCallback } from "react"
import { useSelector } from "react-redux"
import { Link } from "react-router-dom"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos"
import PredefinedDateRanges from "../../../core/common/datePicker"
import CommonSelect, { Option } from "../../../core/common/commonSelect"
import Table from "../../../core/common/dataTable/index"
import TooltipOption from "../../../core/common/tooltipOption"
import TransportModal from "./transportModal"
import {
  getAllDrivers,
  Driver,
  createDriver,
  updateDriver,
} from "../../../service/transport/index"
import { all_routes } from "../../router/all_routes"
import { toast } from "react-toastify"
import { SORT_OPTIONS } from "../../../core/common/selectoption/selectoption"
import { statusOptions } from "../../../core/common/selectoption/selectoption"
import { printTable, exportTableToPdf, exportTableToExcel } from "../../../utils/exportTable"

const formatCnic = (value: string): string => {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 13)
  if (digits.length <= 5) return digits
  if (digits.length <= 12) return `${digits.slice(0, 5)}-${digits.slice(5)}`
  return `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12)}`
}

const getContractorNames = (assignments: any[]): string => {
  if (!assignments?.length) return "N/A"
  const seen: Record<string, boolean> = {}
  const names = assignments
    .map((a: any) => a.vehicleId?.contractorId?.name)
    .filter((n: any): n is string => Boolean(n) && !seen[n] && (seen[n] = true))
  return names.length ? names.join(", ") : "N/A"
}

const EXPORT_HEADERS = ["ID", "Name", "Phone", "CNIC", "License No", "Contractor", "Vehicle(s)", "Address", "Status"]

const toExportRows = (rows: Driver[]): string[][] =>
  rows.map(d => [
    d.id || "",
    d.name || "",
    d.phone || "",
    formatCnic(d.cnic || ""),
    d.driverLicenseNo || "",
    getContractorNames(d.assignments || []),
    d.assignments?.map((a: any) => a.vehicleId?.vehicleNo).filter(Boolean).join(", ") || "N/A",
    d.address || "",
    d.status || "",
  ])

const mapDriver = (driver: any): Driver => ({
  ...driver,
  key: driver._id,
  id: driver.driverID || driver._id,
  name: `${driver.first_name || ""} ${driver.last_name || ""}`.trim() || driver.driverID,
  phone: driver.contact_number,
  driverLicenseNo: driver.license_number,
  cnic: driver.cnic_number || driver.cnic,
  address: driver.address,
  status: driver.status,
  statusClass: driver.status === "active" ? "badge-soft-success" : "badge-soft-danger",
  driverImg: driver.img || "assets/img/students/student-01.jpg",
  first_name: driver.first_name,
  last_name: driver.last_name,
  contact_number: driver.contact_number,
  license_number: driver.license_number,
  joining_date: driver.joining_date,
  assignments: driver.assignments,
  licenseImage: driver.licenseImage,
  cnicImage: driver.cnicImage,
})

const TransportVehicleDrivers = () => {
  const routes = all_routes
  const currentBranch = useSelector(selectCurrentBranch)
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null)
  const [data, setData] = useState<Driver[]>([])
  const [loading, setLoading] = useState(true)
  const [totalCount, setTotalCount] = useState(0)
  const [searchText, setSearchText] = useState("")
  const [sortBy, setSortBy] = useState(SORT_OPTIONS[0])
  const [filters, setFilters] = useState<{ status?: Option }>({})
  const [appliedFilters, setAppliedFilters] = useState<{ status?: string }>({})
  const [driverToDelete, setDriverToDelete] = useState<string | null>(null)
  const [driverToEdit, setDriverToEdit] = useState<Driver | null>(null)
  const [printLoading, setPrintLoading] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [excelLoading, setExcelLoading] = useState(false)
  // Driver cannot be deleted (backend has no DELETE) – delete state kept for modal compatibility only

  const activeFilters = {
    searchText,
    status: appliedFilters?.status || "",
    sortBy: sortBy.value.sortBy,
    sortOrder: sortBy.value.sortOrder,
  }

  const fetchDrivers = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getAllDrivers(activeFilters)
      const mappedDrivers = response.data.map(mapDriver)
      setData(mappedDrivers)
      setTotalCount(response.pagination?.total || mappedDrivers.length)
    } catch (err) {
      toast.error("Failed to fetch drivers")
    } finally {
      setLoading(false)
    }
  }, [
    searchText,
    appliedFilters?.status,
    sortBy.value.sortBy,
    sortBy.value.sortOrder,
  ])

  const fetchAllForExport = async (): Promise<Driver[]> => {
    const response = await getAllDrivers({ ...activeFilters, limit: totalCount || 500, page: 1 })
    return response.data.map(mapDriver)
  }

  useEffect(() => {
    fetchDrivers()
  }, [fetchDrivers])

  const handleApplyFilters = () => {
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show")
    }
    setAppliedFilters({
      status: filters?.status?.value,
    })
  }

  const handleResetFilters = () => {
    setFilters({})
    setAppliedFilters({})
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show")
    }
  }

  const handleSortSelect = (item: (typeof SORT_OPTIONS)[0]) => {
    setSortBy(item)
  }

  const handleAddDriver = async (driverData: {
    first_name: string
    last_name?: string
    contact_number: string
    license_number?: string
    cnic?: string
    cnic_number?: string
    address: string
    status?: string
    joining_date?: string | null
    assignments: { vehicleId: string; shiftId: string; route: string }[]
  }): Promise<boolean | string> => {
    try {
      const response = await createDriver(driverData)

      if (
        response.success ||
        response._id ||
        response.id ||
        response.status === "success"
      ) {
        toast.success("Driver added successfully!")
        fetchDrivers()
        return (response as any)?._id ?? (response as any)?.id ?? true
      } else {
        const errorMessage =
          response.message || response.error || "Failed to add driver"
        toast.error(errorMessage)
        return false
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to add driver"
      toast.error(`Error: ${errorMessage}`)
      return false
    }
  }

  const handleDeleteDriver = async (_driverId: string): Promise<boolean> => {
    toast.info(
      "Driver cannot be deleted. Use Edit and set Status to Inactive to deactivate.",
    )
    return false
  }

  const handleEditDriver = async (
    driverId: string,
    driverData: {
      first_name: string
      last_name?: string
      contact_number: string
      license_number?: string
      address: string
      status?: string
      joining_date?: string | null
      assignments: { vehicleId: string; shiftId: string; route: string }[]
    },
  ): Promise<boolean> => {
    try {
      const response = await updateDriver(driverId, driverData)
      if (
        response.success ||
        response._id ||
        response.id ||
        response.status === "success"
      ) {
        toast.success("Driver updated successfully!")
        fetchDrivers()
        return true
      } else {
        const errorMessage =
          response.message || response.error || "Failed to update driver"
        toast.error(errorMessage)
        return false
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to update driver"
      toast.error(`Error: ${errorMessage}`)
      return false
    }
  }

  const handlePrint = async () => {
    if (!totalCount) { toast.warning("No data to print"); return }
    setPrintLoading(true)
    try {
      const all = await fetchAllForExport()
      printTable("Drivers", EXPORT_HEADERS, toExportRows(all), {
        logoUrl: currentBranch?.blackLogoUrl,
        brandName: campusBrandName(currentBranch),
        branchLabel: campusBranchLabel(currentBranch),
      })
    } catch {
      toast.error("Failed to prepare print data")
    } finally {
      setPrintLoading(false)
    }
  }

  const handleExportPdf = async () => {
    if (!totalCount) { toast.warning("No data to export"); return }
    setPdfLoading(true)
    try {
      const all = await fetchAllForExport()
      exportTableToPdf("Drivers", EXPORT_HEADERS, toExportRows(all), "drivers.pdf", "landscape")
    } catch {
      toast.error("Failed to export PDF")
    } finally {
      setPdfLoading(false)
    }
  }

  const handleExportExcel = async () => {
    if (!totalCount) { toast.warning("No data to export"); return }
    setExcelLoading(true)
    try {
      const all = await fetchAllForExport()
      exportTableToExcel(
        all.map(d => ({
          ID: d.id,
          Name: d.name,
          Phone: d.phone,
          CNIC: formatCnic(d.cnic || ""),
          "License No": d.driverLicenseNo,
          Contractor: getContractorNames(d.assignments || []),
          "Vehicle(s)": d.assignments?.map((a: any) => a.vehicleId?.vehicleNo).filter(Boolean).join(", ") || "N/A",
          Address: d.address,
          Status: d.status,
        })),
        "Drivers",
        "drivers.xlsx"
      )
    } catch {
      toast.error("Failed to export Excel")
    } finally {
      setExcelLoading(false)
    }
  }

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      render: (text: string) => (
        <Link to="#" className="link-primary">
          {text}
        </Link>
      ),
      sorter: (a: Driver, b: Driver) => a.id.localeCompare(b.id),
    },
    {
      title: "Driver",
      dataIndex: "name",
      render: (text: string) => (
        <p className="text-dark mb-0">
          <Link to="#">{text}</Link>
        </p>
      ),
      sorter: (a: Driver, b: Driver) => a.name.localeCompare(b.name),
    },
    {
      title: "Phone Number",
      dataIndex: "phone",
      sorter: (a: Driver, b: Driver) => a.phone.localeCompare(b.phone),
    },
    {
      title: "CNIC",
      dataIndex: "cnic",
      render: (cnic: string) => formatCnic(cnic || ""),
      sorter: (a: Driver, b: Driver) =>
        (a.cnic || "").localeCompare(b.cnic || ""),
    },
    {
      title: "Driver License No",
      dataIndex: "driverLicenseNo",
      sorter: (a: Driver, b: Driver) =>
        a.driverLicenseNo.localeCompare(b.driverLicenseNo),
    },

    {
      title: "Contractor",
      dataIndex: "contractor",
      render: (_: any, driver: Driver) => getContractorNames(driver.assignments || []),
      sorter: (a: Driver, b: Driver) =>
        getContractorNames(a.assignments || []).localeCompare(getContractorNames(b.assignments || [])),
    },
    {
      title: "Vehicle No",
      dataIndex: "vehicleNumbers",
      render: (_: any, driver: Driver) => {
        if (!driver.assignments?.length) return "N/A"
        // Group shifts by vehicle
        const vehicleMap: Record<string, string[]> = {}
        driver.assignments.forEach((a: any) => {
          const vNo = a.vehicleId?.vehicleNo
          const shift = a.shiftId?.name || a.shiftId?.transportShiftId
          if (!vNo) return
          if (!vehicleMap[vNo]) vehicleMap[vNo] = []
          if (shift && !vehicleMap[vNo].includes(shift)) vehicleMap[vNo].push(shift)
        })
        const entries = Object.entries(vehicleMap)
        if (!entries.length) return "N/A"
        return (
          <div>
            {entries.map(([vNo, shifts]) => (
              <div key={vNo}>
                <span className="fw-medium">{vNo}</span>
                {shifts.length > 0 && (
                  <span className="text-muted ms-1">({shifts.join(", ")})</span>
                )}
              </div>
            ))}
          </div>
        )
      },
      sorter: (a: Driver, b: Driver) => {
        const toStr = (d: Driver) =>
          d.assignments?.map((v: any) => v.vehicleId?.vehicleNo).filter(Boolean).join(", ") || ""
        return toStr(a).localeCompare(toStr(b))
      },
    },

    {
      title: "Address",
      dataIndex: "address",
      sorter: (a: Driver, b: Driver) => a.address.localeCompare(b.address),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: string) => (
        <span
          className={`badge ${status === "active" ? "badge-soft-success" : "badge-soft-danger"} d-inline-flex align-items-center`}
        >
          <i className="ti ti-circle-filled fs-5 me-1"></i>
          {status === "active" ? "Active" : "Inactive"}
        </span>
      ),
      sorter: (a: Driver, b: Driver) => a.status.localeCompare(b.status),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: Driver) => (
        <>
          <div className="d-flex align-items-center">
            <div className="dropdown">
              <Link
                to="#"
                className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="ti ti-dots-vertical fs-14" />
              </Link>
              <ul className="dropdown-menu dropdown-menu-right p-3">
                <li>
                  <Link
                    className="dropdown-item rounded-1"
                    to="#"
                    data-bs-toggle="modal"
                    data-bs-target="#edit_driver"
                    onClick={() => setDriverToEdit(record)}
                  >
                    <i className="ti ti-edit-circle me-2" />
                    Edit
                  </Link>
                </li>
                {record.licenseImage && (
                  <li>
                    <a
                      className="dropdown-item rounded-1"
                      href={record.licenseImage}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="ti ti-id-badge me-2" />
                      View License
                    </a>
                  </li>
                )}
                {record.cnicImage && (
                  <li>
                    <a
                      className="dropdown-item rounded-1"
                      href={record.cnicImage}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="ti ti-credit-card me-2" />
                      View CNIC
                    </a>
                  </li>
                )}
                {/* Driver cannot be deleted – use Status: Inactive to deactivate */}
              </ul>
            </div>
          </div>
        </>
      ),
    },
  ]

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Drivers</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Management</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Drivers
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption
                onRefresh={fetchDrivers}
                onPrint={handlePrint}
                onExportPdf={handleExportPdf}
                onExportExcel={handleExportExcel}
                printLoading={printLoading}
                exportPdfLoading={pdfLoading}
                exportExcelLoading={excelLoading}
              />
              <div className="mb-2">
                <Link
                  to="#"
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#add_driver"
                >
                  <i className="ti ti-square-rounded-plus me-2" />
                  Add Drivers
                </Link>
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Drivers List</h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="ti ti-filter me-2" /> Filter
                  </Link>
                  <div
                    className="dropdown-menu drop-width"
                    ref={dropdownMenuRef}
                  >
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 border-bottom">
                        <div className="mb-3">
                          <label className="form-label">Status</label>
                          <CommonSelect
                            className="select"
                            options={statusOptions}
                            value={filters?.status}
                            onChange={val =>
                              setFilters({ status: val as Option })
                            }
                          />
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <div
                          className="btn btn-light me-3"
                          onClick={handleResetFilters}
                        >
                          Reset
                        </div>
                        <div
                          className="btn btn-primary"
                          onClick={handleApplyFilters}
                        >
                          Apply
                        </div>
                      </div>
                    </form>
                  </div>
                </div>

                <div className="dropdown mb-3">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-sort-ascending-2 me-2" />
                    Sort by {sortBy.label}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    {SORT_OPTIONS.map((item, index) => (
                      <li key={index}>
                        <div
                          className={`dropdown-item rounded-1 ${item.label === sortBy.label ? "active" : ""}`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSortSelect(item)}
                        >
                          {item.label}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="card-body p-0 py-3">
              <Table
                setSearchText={setSearchText}
                dataSource={data}
                columns={columns}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>

      <TransportModal
        onAddDriver={handleAddDriver}
        onDeleteDriver={handleDeleteDriver}
        onEditDriver={handleEditDriver}
        driverToDelete={driverToDelete}
        setDriverToDelete={setDriverToDelete}
        driverToEdit={driverToEdit}
        setDriverToEdit={setDriverToEdit}
      />
    </>
  )
}

export default TransportVehicleDrivers
